import { api } from '@/api/ApiGateway.js'
import { useAuthStore } from '@/stores/authStore.js'
import { echoes } from '@/composables/echo/useEchoes.js'

class DocumentsApi
{
	constructor()
	{
		this.params = []
	}

	async download(page, documents, tenantName, organizationName, onComplete, onError)
	{
		const authStore = useAuthStore()
		const headers = {}
		headers['Authorization'] = `Bearer ${authStore.rawJwt}`
		headers['x-client-user-id'] = authStore.getAuth?.user()?.id
		headers['x-jwt-version-code'] = authStore.getAuth?.user()?.jwt_version_code
		headers['X-Socket-ID'] = echoes.socketId();

		const documentIds = documents.map((document) => document.id)
		const documentNames = documents.map((document) => document.file_name)

		const response = await api.axiosInstance.get('/documents/download', {
			params: {
				'document_ids': documentIds,
				'tenant_name': tenantName,
				'organization_name': organizationName,
				'organization_id': authStore.activeOrganizationId,
				'page': page
			},
			responseType: 'blob',
			headers: headers
		})
		.catch(err => {
			console.error('Download error:', err);
			onError(err)
		});

		if (!response)
		{
			return;
		}

		const blobUrl = window.URL.createObjectURL(new Blob([response.data]))

		// Force the file to download (hopefully) rather than open in a new window
		const link = document.createElement('a')
		link.href = blobUrl

		if (documentIds.length > 1) {
			link.setAttribute('download', 'documents.zip')
		}
		else {
			link.setAttribute('download', documentNames[0])
		}
		document.body.appendChild(link)
		link.click()
		link.remove()
		window.URL.revokeObjectURL(blobUrl)

		onComplete()
	}

	/**
	 * Loads tags for the various forms;
	 * @param paginate
	 * @param onSuccess
	 * @param onError
	 */
	loadTags(paginate, onSuccess, onError)
	{
		api.get('documents/tags', {'paginate': paginate}).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error loading tag data.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Create a tag
	 * @param tag
	 * @param onSuccess
	 * @param onError
	 */
	createTag(tag, onSuccess, onError)
	{
		api.post('documents/tags', {
			tag: {
				name: tag.text
			}
		}).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error creating tag.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Updates a tag
	 * @param tag
	 * @param onSuccess
	 * @param onError
	 */
	updateTag(tag, onSuccess, onError)
	{
		api.patch('documents/tags', {
			tag: {
				id: tag.id,
				name: tag.text
			},
		}).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error updating tag.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Disables a tag
	 * @param tagId
	 * @param onSuccess
	 * @param onError
	 */
	disableTag(tagId, onSuccess, onError)
	{
		api.delete(`documents/tags/${tagId}`).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error disabling tag.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Restores a tag
	 * @param tagId
	 * @param onSuccess
	 * @param onError
	 */
	enableTag(tagId, onSuccess, onError)
	{
		api.post(`documents/tags/restore/${tagId}`).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error restoring tag.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	/**
	 * Sets a document to submitted
	 * @param {object} p_data
	 * @param p_on_success
	 * @param p_on_error
	 */
	submitDocument(p_data, p_on_success, p_on_error)
	{
		api.post('documents/status/submit', p_data).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error submitting document.' })
				p_on_error(response)
				return
			}

			p_on_success(response.body()?.data)
		})
	}

	/**
	 * Sets a document to published
	 * @param {object} p_data
	 * @param p_on_success
	 * @param p_on_error
	 */
	publishDocument(p_data, p_on_success, p_on_error)
	{
		api.post('documents/status/publish', p_data).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error publishing document.' })
				p_on_error(response)
				return
			}

			p_on_success(response.body()?.data)
		})
	}

	/**
	 * Sets a document to deleted
	 * @param {object} p_data
	 * @param p_on_success
	 * @param p_on_error
	 */
	deleteDocument(p_data, p_on_success, p_on_error)
	{
		api.post('documents/status/delete', p_data).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error deleting document.' })
				p_on_error(response)
				return
			}

			p_on_success(response.body()?.data)
		})
	}

	/**
	 * Sets a document to deleted
	 * @param {object} p_data
	 * @param p_on_success
	 * @param p_on_error
	 */
	rejectDocument(p_data, p_on_success, p_on_error)
	{
		api.post('documents/status/reject', p_data).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error rejecting document.' })
				p_on_error(response)
				return
			}

			p_on_success(response.body()?.data)
		})
	}

	/**
	 * Sets authorized users for a document using known user ids
	 * @param {int} p_document_id
	 * @param {array} p_authorized_users_ids
	 * @param p_on_success
	 * @param p_on_error
	 */
	addAuthorizedUsersById(p_document_id, p_authorized_users_ids, p_on_success, p_on_error)
	{
		api.post(`documents/${p_document_id}/authorized-users`, {
				'authorized_users_ids': p_authorized_users_ids,
			}).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}

			p_on_success()
		})
	}

	/**
	 * Sets authorized users for a document using only emails.
	 * These users will be looked up by email before they are added.
	 * @param {int} p_document_id
	 * @param {array} p_emails
	 * @param p_on_success
	 * @param p_on_error
	 */
	addAuthorizedUsersByEmail(p_document_id, p_emails, p_on_success, p_on_error)
	{
		api.post(`documents/${p_document_id}/authorized-users`, {
				'emails': p_emails
			}).then(response =>
		{
			if (response.hasError())
			{
				p_on_error(response)
				return
			}

			p_on_success(response.body())
		})
	}

	/**
	 * Validate the emails to make sure they belong to existing users
	 * @param {array} p_emails
	 * @param p_response
	 */
	validateUserEmails(p_emails, p_response)
	{
		api.post(`documents/authorized-users/validate-emails`, {
				'emails': p_emails
			}).then(response =>
		{
			p_response(response.body())
		})
	}
}

export const documentsApi = new DocumentsApi()